import * as React from 'react'
import { useRef, useCallback, useMemo } from 'react'
import { makeStyles, createStyles, Theme } from '@material-ui/core'
import { primaryBlue } from '../../styles/material-ui-theme-v2'
import { tokens } from '../../styles/tokens'

interface YumVerificationCodeInputProps {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  error?: boolean
  disabled?: boolean
  autoFocus?: boolean
  length?: number
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      margin: '8px 0',
    },
    box: {
      width: 0,
      height: 56,
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 56,
      // 1px border + 1px horizontal margin so the focused 2px border + 0 margin occupies the
      // same horizontal space. Vertical margin stays 0 to keep row alignment intact.
      border: `1px solid ${tokens.border.default}`,
      margin: '0 1px',
      borderRadius: 4,
      fontSize: '1.5rem',
      fontWeight: 500,
      textAlign: 'center',
      backgroundColor: 'white',
      color: '#151515',
      outline: 'none',
      transition: 'border-color 120ms ease',
      fontVariantNumeric: 'tabular-nums',
      '&:focus': {
        border: `2px solid ${primaryBlue}`,
        margin: 0,
      },
      '&:disabled': {
        backgroundColor: '#F2F2F2',
      },
    },
    boxError: {
      borderColor: '#E53935',
      '&:focus': {
        border: '2px solid #E53935',
        margin: 0,
      },
    },
  }),
)

/**
 * Six-box numeric verification code input. Each box accepts a single digit.
 * Supports paste, backspace, and arrow-key navigation.
 */
const YumVerificationCodeInput: React.FC<YumVerificationCodeInputProps> = ({
  value,
  onChange,
  onComplete,
  error,
  disabled,
  autoFocus,
  length = 6,
}) => {
  const classes = useStyles()
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const digits = useMemo(() => {
    const padded = (value || '').slice(0, length)
    return Array.from({ length }, (_, i) => padded[i] || '')
  }, [value, length])

  const setValueAndNotify = useCallback(
    (next: string) => {
      const clean = next.replace(/\D/g, '').slice(0, length)
      onChange(clean)
      if (onComplete && clean.length === length) {
        onComplete(clean)
      }
    },
    [onChange, onComplete, length],
  )

  const focusIndex = (index: number) => {
    const target = inputRefs.current[index]
    if (target) {
      target.focus()
      target.select()
    }
  }

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '')
    if (!raw) {
      // Cleared the current box.
      const next = digits.slice()
      next[index] = ''
      setValueAndNotify(next.join(''))
      return
    }
    // If the user pasted or typed multiple digits, spread them across boxes.
    if (raw.length > 1) {
      const combined = (digits.slice(0, index).join('') + raw).slice(0, length)
      setValueAndNotify(combined)
      const nextFocus = Math.min(combined.length, length - 1)
      focusIndex(nextFocus)
      return
    }
    const next = digits.slice()
    next[index] = raw
    setValueAndNotify(next.join(''))
    if (index < length - 1) {
      focusIndex(index + 1)
    }
  }

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        // Clear current box; stay focused.
        const next = digits.slice()
        next[index] = ''
        setValueAndNotify(next.join(''))
        event.preventDefault()
      } else if (index > 0) {
        // Move focus back and clear the previous box.
        const next = digits.slice()
        next[index - 1] = ''
        setValueAndNotify(next.join(''))
        focusIndex(index - 1)
        event.preventDefault()
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusIndex(index - 1)
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      focusIndex(index + 1)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    event.preventDefault()
    setValueAndNotify(pasted)
    const nextFocus = Math.min(pasted.length, length - 1)
    focusIndex(nextFocus)
  }

  return (
    <div className={classes.root}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type="text"
          name={`verification-code-digit-${index + 1}`}
          inputMode="numeric"
          pattern="[0-9]*"
          value={digit}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          autoComplete="one-time-code"
          className={`${classes.box} ${error ? classes.boxError : ''}`}
          aria-label={`Digit ${index + 1}`}
          aria-required="true"
        />
      ))}
    </div>
  )
}

export default YumVerificationCodeInput
